import cx from "classnames";
import Image from "next/image";
import { useState } from "react";

import { ProductPageProps } from "app/smartphone/[slug]/[reference]/page";
import WhiteCrossSVG from "assets/white-cross.svg";
import YellowCheckSVG from "assets/yellow-check.svg";
import { Button } from "ui/Button";
import { Card } from "ui/Card";
import { HorizontalSeparator } from "ui/HorizontalSeparator";
import { LabeledValue } from "ui/LabeledValue";
import { useI18n } from "utils/with-i18n.utils";

type Insurance = "breakage" | "theft";

type InsuranceSectionProps = {
  insuranceFees: ProductPageProps["insuranceFees"];
};

export const InsuranceSection = ({
  insuranceFees: { breakageFeePrice, theftFeePrice },
}: InsuranceSectionProps) => {
  const i18n = useI18n();

  const [insurance, setInsurance] = useState<Insurance>("breakage");

  const hasSelectedTheft = insurance === "theft";
  const hasSelectedBreakage = insurance === "breakage";

  const handleShowTheftInsurance = () => setInsurance("theft");
  const handleShowBreakageInsurance = () => setInsurance("breakage");

  return (
    <section className="flex bg-cornflower-blue">
      <div className="hidden relative basis-1/2 lg:block">
        <Image
          src="/images/shared/replace-section/side-image.webp"
          alt="side image"
          layout="fill"
          sizes="33vw"
          objectFit="cover"
          priority
        />
      </div>

      <div className="flex justify-center w-full">
        <article className="flex flex-col items-center my-14 w-full max-w-5xl lg:py-24">
          <h2 className="mx-7 mb-9 text-4xl font-medium text-center text-white lg:mx-40 lg:mb-24 lg:text-6xl lg:text-left">
            {i18n.t("product.insurance.title")}
          </h2>
          <div className="relative w-full h-80 lg:hidden">
            <Image
              src="/images/shared/replace-section/side-image.webp"
              alt="side image"
              layout="fill"
              objectFit="cover"
              objectPosition="top"
              priority
            />
          </div>
          <div className="flex flex-col gap-x-9 lg:flex-row">
            <div className="flex gap-x-3 my-9 lg:hidden">
              <Button
                onClick={handleShowBreakageInsurance}
                alternative={hasSelectedBreakage ? "primary" : "secondary"}
              >
                {i18n.t("product.insurance.list.break")}
                {i18n.t("product.insurance.break")}
              </Button>

              <Button
                onClick={handleShowTheftInsurance}
                alternative={hasSelectedTheft ? "primary" : "secondary"}
              >
                {i18n.t("product.insurance.list.theft")}
                {i18n.t("product.insurance.theft")}
              </Button>
            </div>
            <article
              className={cx(
                "transition-all duration-150 lg:opacity-100 lg:visible",
                hasSelectedTheft && "opacity-0 translate-y-1/2 invisible h-0",
              )}
            >
              <p className="hidden mb-5 text-2xl text-white lg:block">
                {i18n.t("product.insurance.list.break")}
                <span className="font-bold text-yellow">
                  {i18n.t("product.insurance.break")}
                </span>
              </p>

              <Card
                border="filled"
                className="p-6 bg-white rounded-lg lg:block"
              >
                <ul className="flex flex-col gap-y-4">
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.anyBreak")}
                  </li>
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.liquid")}
                  </li>
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.third")}
                  </li>
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.world")}
                  </li>
                  <li className="flex gap-x-3">
                    <WhiteCrossSVG />
                    {i18n.t("product.insurance.excludedBreak")}
                  </li>
                </ul>
                {!!breakageFeePrice && (
                  <>
                    <HorizontalSeparator className="my-7 w-72" />

                    <LabeledValue
                      label={i18n.t("product.insurance.footerDesc")}
                      value={breakageFeePrice}
                    />
                  </>
                )}
              </Card>
            </article>
            <article
              className={cx(
                "transition-all duration-150 lg:opacity-100 lg:visible",
                hasSelectedBreakage &&
                  "opacity-0 translate-y-1/2 invisible h-0",
              )}
            >
              <p className="hidden mb-5 text-2xl text-white lg:block">
                {i18n.t("product.insurance.list.theft")}
                <span className="font-bold text-yellow">
                  {i18n.t("product.insurance.theft")}
                </span>
              </p>

              <Card
                border="filled"
                className="p-6 bg-white rounded-lg lg:block"
              >
                <ul className="flex flex-col gap-y-4">
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.robber")}
                  </li>
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.thief")}
                  </li>
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.thief2")}
                  </li>
                  <li className="flex gap-x-3">
                    <YellowCheckSVG />
                    {i18n.t("product.insurance.world")}
                  </li>
                  <li className="flex gap-x-3">
                    <WhiteCrossSVG />
                    {i18n.t("product.insurance.excludedTheft")}
                  </li>
                </ul>
                {!!theftFeePrice && (
                  <>
                    <HorizontalSeparator className="my-7 w-72" />

                    <LabeledValue
                      label={i18n.t("product.insurance.footerDesc")}
                      value={theftFeePrice}
                    />
                  </>
                )}
              </Card>
            </article>
          </div>
        </article>
      </div>
    </section>
  );
};
